import {
  compose,
  find,
  identity,
  isEmpty,
  not,
  pathOr,
  prop,
  propOr,
  props,
} from 'ramda';
import { createSelector } from 'reselect';

import { DIALOG_CLOSE_TIMER_IN_MILLISECONDS, NAVIGATION_METHODS } from '@ezugi/constants';
import { DeviceInfo, isPopup, ss } from '@ezugi/utils';

import { RootState } from '../types';
import { canPlaceBetsSelector } from './round';
import { userSelector } from './user';

const VITE_IS_BUNDLE = import.meta.env.VITE_IS_BUNDLE;

export const configSelector = (state: RootState) => state.config ?? {};

export const gameResultsConfigSelector = createSelector(
  configSelector,
  prop('gameResults'),
);

export const winnerListConfigSelector = createSelector(
  configSelector,
  (config) => config.winnerList
);

export const definitionsSelector = createSelector(
  configSelector,
  (config) => {
    const definitions = config?.definitions || {};

    /**
     * Apple requires all assets to be static in iOS bundles
     * See https://developer.apple.com/news/?id=12232019b
     */
    if (VITE_IS_BUNDLE) {
      definitions.OPERATOR_ASSETS = '../CustomAssets/';
    }

    return definitions;
  }
);

export const areOutgoingCallsEnabledSelector = createSelector(
  configSelector,
  (config) => config.outgoingCalls
);

export const preloaderSelector = createSelector(
  configSelector,
  prop('preloader'),
);

export const generalConfigSelector = createSelector(
  configSelector,
  (config) => config.general
);

// Assert config by checking that we have fields in generalConfig
export const isConfigLoadedSelector = createSelector(
  configSelector,
  compose(not, isEmpty, prop('general')),
);

export const isEvoPlayerSelector = createSelector(
  generalConfigSelector,
  (general) => Boolean(general.isEvoPlayer)
);

export const initialQualitySelector = createSelector(
  generalConfigSelector,
  (generalConfig) => generalConfig.evoplayerInitialQuality ?? ''
);

export const useDynamicVideoDelaySelector = createSelector(
  generalConfigSelector,
  (generalConfig) => generalConfig.useDynamicVideoDelay ?? false
);

export const incidentDialogScreenTimeSelector = createSelector(
  generalConfigSelector,
  (generalConfig) => generalConfig.incidentDialogScreenTime ?? DIALOG_CLOSE_TIMER_IN_MILLISECONDS
);

export const abrAdaptionRuleSelector = createSelector(
  generalConfigSelector,
  (generalConfig) => generalConfig.abrAdaptionRule ?? 'deviationOfMean'
);

export const widgetsSelector = createSelector(configSelector, ({ widgets }) => widgets);
export const canOpenMiniLobbySelector = createSelector(widgetsSelector, prop('minilobby'));
export const goHomeEnabledSelector = createSelector(widgetsSelector, (widgets) => widgets?.home);
export const chatEnabledSelector = createSelector(widgetsSelector, (widgets) => widgets?.chat);
export const liveSupportEnabledSelector = createSelector(widgetsSelector, (widgets) => widgets?.liveSupport);
export const miniLobbyEnabledSelector = createSelector(widgetsSelector, (widgets) => widgets?.minilobby);
export const fullscreenEnabledSelector = createSelector(widgetsSelector, (widgets) => widgets?.fullscreen);
export const cameraAngleEnabledSelector = createSelector(widgetsSelector, (widgets) => widgets?.cameraAngleChange);
export const canGoHomeSelector = createSelector(widgetsSelector, (w) => w?.home || !!ss.get('home_url')[1]);
export const cashierWidgetSelector = createSelector(widgetsSelector, prop('cashier'));

export const skinSelector = createSelector(configSelector, prop('skin'));
export const defaultOpenPayoutsSelector = createSelector(
  widgetsSelector,
  propOr(false, 'payoutsDefaultOpen')
);

/**
 * @deprecated DON'T USE, WILL BE DELETED
 */
export const skinColorsLegacySelector = createSelector(
  configSelector,
  propOr({}, 'skinColors'),
);

export const noBackgroundToolbarSelector = createSelector(
  generalConfigSelector,
  (generalConfig) => generalConfig.noBackgroundToolbar ?? false
);

export const formatSelector = createSelector(configSelector, prop('format'));
export const authSelector = createSelector(configSelector, prop('authParams'));

export const cashierSelector = createSelector(configSelector, prop('cashier'));
export const chatConfigSelector = createSelector(configSelector, prop('chat'));

const allowedCashierNavMethods = [
  NAVIGATION_METHODS.POPUP,
  NAVIGATION_METHODS.TAB,
];

export const isCashierEnabledSelector = createSelector(
  widgetsSelector,
  cashierSelector,
  (widgets, cashier) => {
    const cashierRequestMethod = cashier?.cashierMethod ?? 'popup';

    return Boolean(
      !!widgets?.cashier
        && !!cashier?.cashierUrl
        && !(isPopup() && !allowedCashierNavMethods.includes(cashierRequestMethod))
    );
  }
);

export const rouletteConfigSelector = createSelector(
  configSelector,
  prop('roulette'),
);

export const gameTypesSelector = createSelector(
  configSelector,
  prop('gameTypes'),
);

export const payoutsSelector = createSelector(
  configSelector,
  (config) => config.payouts ?? {}
);

export const filtersSelector = createSelector(configSelector, prop('filters'));
export const betsConfigSelector = createSelector(configSelector, prop('bets'));

export const tipsOnResultSelector = createSelector(
  generalConfigSelector,
  (generalConfig) => generalConfig.tipsOnResult
);

export const showGameInfoVersionSelector = createSelector(
  generalConfigSelector,
  (general) => general.showGameInfoVersion
);

export const operatorLogoSelector = createSelector(
  definitionsSelector,
  generalConfigSelector,
  (def, gen) => (gen.enableLogo ? `${def.OPERATOR_ASSETS}${gen.logoUrl}` : ''),
);

export const languagesSelector = createSelector(
  generalConfigSelector,
  (general) => general.availableLanguages ?? []
);

export const allowVideoQualitySwitchSelector = createSelector(
  generalConfigSelector,
  (general) => Boolean(general.allowVideoQualitySwitch)
);

const fallbackLanguageCode = 'en';
const fallbackLanguage = { code: fallbackLanguageCode, label: 'English' };
/**
 * Find current language if provided
 * Fallback to english or first language found
 */
export const currentLanguageSelector = createSelector(
  generalConfigSelector,
  languagesSelector,
  (generalConfig, languages = []) => languages.find(({ code }) => code === generalConfig.language)
    || languages.find(({ code }) => code === fallbackLanguageCode)
    || languages[0]
    || fallbackLanguage,
);

export const lobbyConfigSelector = createSelector(
  configSelector,
  propOr({}, 'lobby'),
);

export const tablesWithoutFlagSelector = createSelector(
  lobbyConfigSelector,
  propOr([], 'hide_table_flag'),
);

export const hideGameVideoTintSelector = createSelector(
  generalConfigSelector,
  (generalConfig) => generalConfig.hideGameVideoTint ?? false
);

/**
 * Select logo based on BO config
 */
export const logoUrlSelector = createSelector(
  definitionsSelector,
  generalConfigSelector,
  (definitions, general) => (general.enableLogo
    ? `${definitions.OPERATOR_ASSETS}${general.logoUrl}`
    : ''),
);

export const backgroundMusicConfigSelector = createSelector(
  generalConfigSelector,
  (boGeneralConfig) => boGeneralConfig.backgroundMusic,
);

export const canBetOnStatisticsSelector = createSelector(
  generalConfigSelector,
  canPlaceBetsSelector,
  (generalConfig, userCanBet) => userCanBet && generalConfig.enableStatisticsBetting,
);

export const desktopBetToolbarConfigSelector = createSelector(
  generalConfigSelector,
  (general) => general.desktopBetToolbarBackground
);

export const desktopBetToolbarPositionSelector = createSelector(
  generalConfigSelector,
  ({ betToolbarXOffset, betToolbarYOffset }) => ({
    betToolbarXOffset,
    betToolbarYOffset,
  }),
);

export const isEvoWrappedSelector = createSelector(
  configSelector,
  (config) => config.isEvoWrapped
);

export const privacyNoticeUrlBJVCSelector = createSelector(
  configSelector,
  generalConfigSelector,
  (config, { language }) => compose(
    // @ts-ignore
    find(identity),
    props([ language, 'en' ]),
    pathOr({}, [ 'blackjack', 'videoChatLegalNoticeUrl' ]),
  )(config) as string,
);

export const showInactivityDialogSelector = createSelector(
  generalConfigSelector,
  (generalConfig) => generalConfig.showInactivityDialog ?? true
);

export const isTutorialVisibleSelector = createSelector(
  generalConfigSelector,
  (generalConfig) => generalConfig.isTutorialVisible ?? true
);

export const maxTutorialDisplaysSelector = createSelector(
  generalConfigSelector,
  (generalConfig) => generalConfig.maxTutorialDisplays
);

// * For desktop we don't need TapToPlay regardless of this Back Office config.
// On iOS for nanoplayer we always require user interaction (issue on low power mode)
export const isTapToPlayVisibleSelector = createSelector(
  generalConfigSelector,
  isEvoPlayerSelector,
  (config, isEvoPlayer) => DeviceInfo.isHandheld
   && ((DeviceInfo.isIOS && !isEvoPlayer) || config.isTapToPlayVisible)
);

export const allowWinBellowBetSelector = createSelector(
  generalConfigSelector,
  (generalConfig) => generalConfig.noWinBelowBet ?? false
);

export const ukgcMethodSelector = createSelector(
  generalConfigSelector,
  (generalConfig) => generalConfig.realityCheckButtonsMethod ?? 'url'
);

export const videoConfigSelector = createSelector(
  authSelector,
  definitionsSelector,
  userSelector,
  (auth, definitions, user) => (auth ? {
    operatorId: Number(auth.operatorId),
    gameId: Number(auth.game_id),
    tableId: Number(auth.table_id),
    identifier: auth.identifier,
    cdnLocation: definitions.VIDEO_CDN_SERVICE,
    playerId: Number(user.sessionId),
  } : {}),
);

// eslint-disable-next-line max-len
export const nameBeforeGreetingSelector = createSelector(generalConfigSelector, ({ nameBeforeGreetingMessage }) => Boolean(nameBeforeGreetingMessage));

/**
 * Selector for the back office progress bar configuration.
 *
 * This selector retrieves the back office progress bar configuration from the general group.
 *
 * The progress bar configuration is stored as a JSON string in the `progressBar` property (BO-attributeId) of the general group.
 * If the `progressBar` property (BO-attributeId) is not present or its value cannot be parsed as JSON,
 * an empty object is returned.
 * Current game implementation: [Bet On Roulette]
 *
 * @example
 * // The selector will return:
 * // {
 * //   "hideTimer": true,
 * //   "lastBetsTimer": 5,
 * //   "waitForResultsTimer": 3
 * // }
 *
 * @returns {Object} The progress bar configuration. Possible properties include `hideTimer`, `lastBetsTimer`, and `waitForResultsTimer`.
 */
export const progressBarConfigSelector = createSelector(
  generalConfigSelector,
  (generalConfig) => generalConfig.progressBar ?? { hideTimer: false }
);

export const complianceConfigSelector = createSelector(
  configSelector,
  (config) => config.compliance ?? {}
);

export const isMinimalBetNotificationEnabledSelector = createSelector(
  complianceConfigSelector,
  (complianceConfig) => complianceConfig.minimal_bet_notification_enabled || false
);

export const isNetPositionVisibleSelector = createSelector(
  complianceConfigSelector,
  (complianceConfig) => complianceConfig.net_position_visible || false
);

export const isStartGameMutedSelector = createSelector(
  complianceConfigSelector,
  (complianceConfig) => complianceConfig.start_game_muted || false
);

export const gamePayoutsRtp = createSelector(
  generalConfigSelector,
  (general) => general.gamePayoutsRTP || ''
);

export const excludedTablesSelector = createSelector(
  configSelector,
  propOr([], 'excludeTables')
);

export const excludedGamesSelector = createSelector(
  configSelector,
  propOr([], 'excludeGames')
);

export const tipsHeaderSelector = createSelector(
  generalConfigSelector,
  propOr(false, 'tipsHeader'),
);
